"use client";

import { useState, useCallback } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "@/components/ui/icon";
import { SearchCriteriaEditor } from "./criteria-editor";

/**
 * Search-side twin of the Arbitrator setup card: same shell, badge, label
 * pill, drag handle and in-place edit — but driven by props instead of the
 * Arbitrator Zustand store.
 */

export interface SearchCriterionCardDraft {
  id: string;
  label: string;
  description: string;
  position: number;
}

export type SearchCriterionCardPosition = "first" | "middle" | "last" | "single";

interface SearchCriteriaCardProps {
  draft: SearchCriterionCardDraft;
  position: SearchCriterionCardPosition;
  onUpdate: (id: string, item: { label: string; description: string }) => void;
  onRemove: (id: string) => void;
  isDragOverlay?: boolean;
}

const EASE = [0.4, 0, 0.2, 1] as const;

function cornerClass(position: SearchCriterionCardPosition) {
  switch (position) {
    case "first":
      return "rounded-b-lg";
    case "last":
      return "rounded-t-lg";
    case "middle":
      return "rounded-lg";
    case "single":
      return "";
  }
}

const SOLID_BG_CLASS = "bg-white dark:bg-[#1f1f21]";
const BORDER_CLASS = "border-contrast/10";

export function SearchCriteriaCard({
  draft,
  position,
  onUpdate,
  onRemove,
  isDragOverlay = false,
}: SearchCriteriaCardProps) {
  const [editing, setEditing] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: draft.id, disabled: isDragOverlay });

  const handleEdit = useCallback(() => setEditing(true), []);
  const handleCancelEdit = useCallback(() => setEditing(false), []);
  const handleRemove = useCallback(
    () => onRemove(draft.id),
    [onRemove, draft.id],
  );
  const handleSaved = useCallback(
    (item: { label: string; description: string }) => {
      onUpdate(draft.id, item);
      setEditing(false);
    },
    [onUpdate, draft.id],
  );

  if (isDragOverlay) {
    return (
      <CardShell position={position}>
        <CardView draft={draft} onEdit={() => {}} onRemove={() => {}} />
      </CardShell>
    );
  }

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.35 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full">
      <motion.div
        className={`relative w-full overflow-hidden border ${BORDER_CLASS} shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${cornerClass(position)}`}
        animate={editing ? "open" : "closed"}
        transition={{ duration: 0.4 }}
      >
        <div
          aria-hidden
          className={`absolute inset-0 pointer-events-none ${cornerClass(position)} ${SOLID_BG_CLASS}`}
        />
        <div
          aria-hidden
          className={`absolute inset-0 pointer-events-none ${cornerClass(position)} dark:shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.1)]`}
        />

        <AnimatePresence mode="popLayout" initial={false}>
          {!editing ? (
            <motion.div
              key="view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.25, ease: EASE }}
              className="relative"
            >
              <CardView
                draft={draft}
                onEdit={handleEdit}
                onRemove={handleRemove}
                dragHandleProps={
                  {
                    ...attributes,
                    ...listeners,
                  } as React.HTMLAttributes<HTMLButtonElement>
                }
              />
            </motion.div>
          ) : (
            <motion.div
              key="edit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.25, ease: EASE }}
              className="relative"
            >
              <SearchCriteriaEditor
                initialLabel={draft.label}
                initialDescription={draft.description}
                onCancel={handleCancelEdit}
                onSaved={handleSaved}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function CardShell({
  position,
  children,
}: {
  position: SearchCriterionCardPosition;
  children: React.ReactNode;
}) {
  const corners = cornerClass(position);
  return (
    <div
      className={`relative w-full overflow-hidden border ${BORDER_CLASS} shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${corners}`}
    >
      <div
        aria-hidden
        className={`absolute inset-0 pointer-events-none ${corners} ${SOLID_BG_CLASS}`}
      />
      <div
        aria-hidden
        className={`absolute inset-0 pointer-events-none ${corners} dark:shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.1)]`}
      />
      {children}
    </div>
  );
}

function CardView({
  draft,
  onEdit,
  onRemove,
  dragHandleProps,
}: {
  draft: SearchCriterionCardDraft;
  onEdit: () => void;
  onRemove: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
}) {
  return (
    <div className="p-4">
      <div className="flex flex-col gap-2.5">
        <div className="flex h-7 items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-label="Drag to reorder"
              {...dragHandleProps}
              className="flex shrink-0 cursor-grab items-center justify-center text-contrast/40 hover:text-contrast/70 transition-colors active:cursor-grabbing"
            >
              <Icon name="IconDotGrid2x3" size={12} />
            </button>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-contrast/4 text-sm font-medium text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]">
              {draft.position}
            </span>
            <span className="inline-flex shrink min-w-0 items-center rounded-full bg-contrast/4 px-2.5 py-1 text-sm font-medium text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]">
              <span className="truncate">{draft.label}</span>
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit criterion"
              className="flex cursor-pointer items-center justify-center text-contrast/50 hover:text-contrast/80 transition-colors p-1.5 -m-1.5 touch-manipulation"
            >
              <Icon name="IconEditBig" size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Delete criterion"
              className="flex cursor-pointer items-center justify-center text-contrast/50 hover:text-contrast/80 transition-colors p-1.5 -m-1.5 touch-manipulation"
            >
              <Icon name="IconTrashCanSimple" size={16} />
            </button>
          </div>
        </div>

        <p className="text-[13px] leading-normal text-contrast/70">
          {draft.description}
        </p>
      </div>
    </div>
  );
}
